import { useEffect, useState } from 'react';
import {
  adminService,
  type UserListItem,
  type CreateUserRequest,
} from '../../services/admin.service';
import './AdminUsers.css';

const ROLE_LABELS: Record<string, string> = {
  User: 'Пользователь',
  Seller: 'Продавец',
  Moderator: 'Модератор',
  Admin: 'Администратор',
};

const ROLE_VALUES = [
  { value: 0, label: 'Пользователь' },
  { value: 1, label: 'Продавец' },
  { value: 2, label: 'Модератор' },
  { value: 3, label: 'Администратор' },
];

export default function AdminUsers() {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [form, setForm] = useState<CreateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 0,
  });

  const loadUsers = () => {
    adminService
      .getUsers()
      .then((res) => {
        if (res.success && res.data) setUsers(res.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleChange = (field: keyof CreateUserRequest, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setMessage(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim()) {
      setMessage({ type: 'err', text: 'Укажите email' });
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setMessage({ type: 'err', text: 'Укажите имя и фамилию' });
      return;
    }
    if (form.password.length < 8) {
      setMessage({ type: 'err', text: 'Пароль не менее 8 символов' });
      return;
    }
    setSubmitLoading(true);
    setMessage(null);
    adminService
      .createUser(form)
      .then((res) => {
        if (res.success) {
          setMessage({ type: 'ok', text: 'Пользователь создан' });
          setForm({ email: '', firstName: '', lastName: '', password: '', role: 0 });
          loadUsers();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка создания' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка создания пользователя' }))
      .finally(() => setSubmitLoading(false));
  };

  return (
    <div className="admin-users">
      <h1>Пользователи</h1>
      <p className="admin-users-desc">Список пользователей и добавление нового</p>

      <div className="admin-users-grid">
        <section className="admin-users-form-section">
          <h2>Добавить пользователя</h2>
          <form className="admin-users-form" onSubmit={handleSubmit}>
            <div className="admin-users-row">
              <label>Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="user@example.com"
                required
              />
            </div>
            <div className="admin-users-row">
              <label>Имя *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div className="admin-users-row">
              <label>Фамилия *</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div className="admin-users-row">
              <label>Пароль * (мин. 8 символов)</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                minLength={8}
                maxLength={100}
              />
            </div>
            <div className="admin-users-row">
              <label>Роль</label>
              <select
                value={form.role}
                onChange={(e) => handleChange('role', Number(e.target.value))}
              >
                {ROLE_VALUES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            {message && (
              <p
                className={
                  message.type === 'ok' ? 'admin-users-msg-ok' : 'admin-users-msg-err'
                }
              >
                {message.text}
              </p>
            )}
            <div className="admin-users-actions">
              <button type="submit" disabled={submitLoading}>
                {submitLoading ? 'Создание...' : 'Создать пользователя'}
              </button>
            </div>
          </form>
        </section>

        <section className="admin-users-list-section">
          <h2>Список пользователей</h2>
          {loading ? (
            <p className="admin-users-loading">Загрузка...</p>
          ) : (
            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Имя</th>
                    <th>Фамилия</th>
                    <th>Роль</th>
                    <th>Дата регистрации</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>{u.email}</td>
                      <td>{u.firstName}</td>
                      <td>{u.lastName}</td>
                      <td>{ROLE_LABELS[u.role] ?? u.role}</td>
                      <td>{new Date(u.createdAt).toLocaleDateString('ru-RU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="admin-users-empty">Нет пользователей</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
